import { Injectable, OnDestroy, signal } from '@angular/core';
import { TokenPair } from './token-pair.model';

const ACCESS_TOKEN_KEY = 'otp_access_token';

@Injectable({ providedIn: 'root' })
export class TokenStorageService implements OnDestroy {
  // Only the access token lives here. The refresh token stays in the httpOnly cookie `to_refresh`,
  // out of reach of script, so an XSS cannot carry the session beyond the access token's lifetime.
  private readonly accessTokenSignal = signal<string | null>(localStorage.getItem(ACCESS_TOKEN_KEY));

  readonly accessToken = this.accessTokenSignal.asReadonly();

  private readonly handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== ACCESS_TOKEN_KEY) {
      return;
    }
    this.accessTokenSignal.set(event.newValue);
  };

  constructor() {
    window.addEventListener('storage', this.handleStorageEvent);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent);
  }

  hasSession(): boolean {
    return this.accessTokenSignal() !== null;
  }

  setTokens(tokens: TokenPair): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    this.accessTokenSignal.set(tokens.accessToken);
  }

  accessTokenExpiresAt(token: string | null = this.accessTokenSignal()): number | null {
    return this.timeClaim(token, 'exp');
  }

  accessTokenIssuedAt(token: string | null): number | null {
    return this.timeClaim(token, 'iat');
  }

  private timeClaim(token: string | null, claim: 'exp' | 'iat'): number | null {
    try {
      const payload = token?.split('.')[1];
      if (!payload) {
        return null;
      }
      const value = (JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>)[claim];
      return typeof value === 'number' ? value * 1000 : null;
    } catch {
      return null;
    }
  }

  // Storage events from other tabs arrive asynchronously; whoever holds the cross-tab refresh lock
  // must see the latest write right away, so this also brings the signal in line with it.
  readStoredAccessToken(): string | null {
    const stored = localStorage.getItem(ACCESS_TOKEN_KEY);
    this.accessTokenSignal.set(stored);
    return stored;
  }

  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    this.accessTokenSignal.set(null);
  }
}
